import {
  buildPayrollRun,
  buildTimesheet,
  buildTimesheets,
  DAY_HOURS,
  daysFor,
  DEFAULT_SETTINGS,
  fmtH,
  hoursOpen,
  isOverlong,
  maxShiftHours,
  normalizeKennitala,
  orlofDayRate,
  orlofYearOf,
  pendingAccruals,
  periodInfo,
  reconcile,
  todayIso,
  type PayrollRun,
  type Adjustment,
  type Company,
  type CompanySettings,
  type EditGrant,
  type EditLogEntry,
  type Employee,
  type LeaveRequest,
  type NotificationEvent,
  type OrlofEntry,
  type OvertimeConfirmation,
  type RequestStatus,
  type Shift,
  type Timesheet,
  type TimesheetStatus,
} from '@klukka/core';
import {
  ApiError,
  type EmployeePatch,
  type EmployeeSnapshot,
  type EmployerSnapshot,
  type HourCorrectionInput,
  type ImportResult,
  type KlukkaApi,
  type NewEmployeeInput,
  type NewRequestInput,
  type OrlofEntryInput,
  type OtpChallenge,
  type PayslipSummary,
  type Portal,
  type SelfServicePatch,
  type Session,
} from './contract.js';
import { buildSeed, type AdminUser, type LocalDb, type StoredPeriod } from './seed.js';

/**
 * Browser-storage implementation of the API.
 *
 * It is a faithful stand-in, not a stub: the same authorisation rules, the same
 * audit trail and the same notification behaviour as the Azure backend, so the
 * portals can be built and reviewed before any infrastructure exists. Swapping
 * in `HttpApi` changes nothing above this line.
 */

const DB_KEY = 'klukka.db.v1';
const SESSION_KEY = 'klukka.session.v1';
const CHALLENGE_KEY = 'klukka.otp.v1';

interface StoredChallenge {
  challengeId: string;
  accountId: string;
  portal: Portal;
  companyId: string;
  empId: string | null;
  name: string;
  code: string;
  firstLogin: boolean;
  expiresAt: string;
}

export interface LocalApiOptions {
  /** Pin "today" so the seeded demo history lines up. Defaults to the real date. */
  today?: string;
  /** Reveal the OTP on screen. Never enable against real data. */
  revealOtp?: boolean;
  storage?: Storage;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function nowIso(): string {
  return new Date().toISOString();
}

export class LocalApi implements KlukkaApi {
  private readonly store: Storage;
  private readonly pinnedToday?: string;
  private readonly revealOtp: boolean;

  constructor(opts: LocalApiOptions = {}) {
    this.store = opts.storage ?? window.localStorage;
    this.pinnedToday = opts.today;
    this.revealOtp = opts.revealOtp ?? true;
  }

  // ---- persistence ----

  private db(): LocalDb {
    const raw = this.store.getItem(DB_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as LocalDb;
        // Bumped when the stored shape changes; an older database is reseeded
        // rather than migrated, because it only ever holds demo data.
        if (parsed.version === 4) return parsed;
      } catch {
        // fall through and reseed
      }
    }
    const seeded = buildSeed();
    this.store.setItem(DB_KEY, JSON.stringify(seeded));
    return seeded;
  }

  private save(db: LocalDb): void {
    this.store.setItem(DB_KEY, JSON.stringify(db));
  }

  /** Wipe local state and reseed — used by the "reset demo data" action. */
  reset(): void {
    this.store.removeItem(DB_KEY);
    this.store.removeItem(SESSION_KEY);
    this.store.removeItem(CHALLENGE_KEY);
  }

  private today(): string {
    return this.pinnedToday ?? todayIso();
  }

  private settingsFor(company: Company): CompanySettings {
    // Defaults first, as the API's loadCompany does, so a browser holding data
    // from before a setting existed still gets a value for it.
    return { ...DEFAULT_SETTINGS, ...company.settings, today: this.today() };
  }

  // ---- session ----

  private readSession(): Session | null {
    const raw = this.store.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const s = JSON.parse(raw) as Session;
      if (new Date(s.expiresAt).getTime() < Date.now()) return null;
      return s;
    } catch {
      return null;
    }
  }

  private requireSession(portal: Portal): Session {
    const s = this.readSession();
    if (!s) throw new ApiError('Not signed in', 401, 'unauthenticated');
    if (s.portal !== portal) throw new ApiError('Wrong portal for this session', 403, 'forbidden');
    return s;
  }

  async currentSession(): Promise<Session | null> {
    return this.readSession();
  }

  async logout(): Promise<void> {
    this.store.removeItem(SESSION_KEY);
    this.store.removeItem(CHALLENGE_KEY);
  }

  async requestOtp(kennitala: string, portal: Portal): Promise<OtpChallenge> {
    const db = this.db();
    const kt = normalizeKennitala(kennitala);
    let account: { id: string; companyId: string; empId: string | null; name: string; phone: string };

    if (portal === 'employer') {
      const admin = db.admins.find((a: AdminUser) => normalizeKennitala(a.kennitala) === kt);
      if (!admin) throw new ApiError('No administrator with that kennitala', 404, 'account_not_found');
      account = { id: admin.id, companyId: admin.companyId, empId: null, name: admin.name, phone: admin.phone };
    } else {
      const emp = db.employees.find((e) => normalizeKennitala(e.kennitala) === kt && !e.inactive);
      if (!emp) throw new ApiError('No employee with that kennitala', 404, 'account_not_found');
      account = { id: emp.id, companyId: emp.companyId, empId: emp.id, name: emp.name, phone: emp.phone };
    }

    const firstLogin = !db.knownAccounts.includes(account.id);
    const code = String(Math.floor(1000 + Math.random() * 9000));
    const challenge: StoredChallenge = {
      challengeId: uid('otp'),
      accountId: account.id,
      portal,
      companyId: account.companyId,
      empId: account.empId,
      name: account.name,
      code,
      firstLogin,
      expiresAt: new Date(Date.now() + 5 * 60_000).toISOString(),
    };
    this.store.setItem(CHALLENGE_KEY, JSON.stringify(challenge));

    return {
      challengeId: challenge.challengeId,
      maskedPhone: `··· ${account.phone.slice(-4)}`,
      firstLogin,
      ...(this.revealOtp && !firstLogin ? { devCode: code } : {}),
    };
  }

  private readChallenge(challengeId: string): StoredChallenge {
    const raw = this.store.getItem(CHALLENGE_KEY);
    if (!raw) throw new ApiError('Verification expired — start again', 410, 'challenge_expired');
    const c = JSON.parse(raw) as StoredChallenge;
    if (c.challengeId !== challengeId) throw new ApiError('Verification expired — start again', 410, 'challenge_expired');
    if (new Date(c.expiresAt).getTime() < Date.now()) throw new ApiError('Verification expired — start again', 410, 'challenge_expired');
    return c;
  }

  private grantSession(c: StoredChallenge): Session {
    const db = this.db();
    if (!db.knownAccounts.includes(c.accountId)) {
      db.knownAccounts.push(c.accountId);
      this.save(db);
    }
    const session: Session = {
      token: uid('tok'),
      portal: c.portal,
      companyId: c.companyId,
      empId: c.empId,
      name: c.name,
      expiresAt: new Date(Date.now() + 12 * 3600_000).toISOString(),
    };
    this.store.setItem(SESSION_KEY, JSON.stringify(session));
    this.store.removeItem(CHALLENGE_KEY);
    return session;
  }

  async completeFirstLogin(challengeId: string): Promise<Session> {
    const c = this.readChallenge(challengeId);
    if (!c.firstLogin) throw new ApiError('This account requires a verification code', 403, 'otp_required');
    return this.grantSession(c);
  }

  async verifyOtp(challengeId: string, code: string): Promise<Session> {
    const c = this.readChallenge(challengeId);
    if (code.trim() !== c.code) throw new ApiError('Wrong code', 401, 'bad_code');
    return this.grantSession(c);
  }

  // ---- shared lookups ----

  private company(db: LocalDb, companyId: string): Company {
    const c = db.companies.find((x) => x.id === companyId);
    if (!c) throw new ApiError('Company not found', 404, 'not_found');
    return c;
  }

  private employeeOf(db: LocalDb, companyId: string, empId: string): Employee {
    const e = db.employees.find((x) => x.id === empId && x.companyId === companyId);
    if (!e) throw new ApiError('Employee not found', 404, 'not_found');
    return e;
  }

  private openShift(db: LocalDb, empId: string): Shift | undefined {
    return db.shifts.find((s) => s.empId === empId && s.clockOutAt === null);
  }

  private activeGrant(db: LocalDb, empId: string): EditGrant | null {
    const g = db.editGrants.find((x) => x.empId === empId);
    return g && g.until >= this.today() ? g : null;
  }

  /**
   * Raise the "still working?" notification once a running shift passes the
   * daily threshold. In Azure this is a timer-triggered function writing to
   * `notification_events`; here it is evaluated when the snapshot is read.
   */
  private ensureOvertimeNotification(db: LocalDb, emp: Employee, settings: CompanySettings): boolean {
    const shift = this.openShift(db, emp.id);
    if (!shift || shift.otConfirmedAt) return false;
    const hours = (Date.now() - new Date(shift.clockInAt).getTime()) / 3600_000;
    if (hours < settings.dailyThreshold) return false;
    const existing = db.notifications.find(
      (n) => n.empId === emp.id && n.kind === 'overtime_reminder' && (n.payload.shiftId as string) === shift.id,
    );
    if (existing) {
      if (!existing.acknowledgedAt) existing.payload.hours = Math.round(hours * 100) / 100;
      return true;
    }
    db.notifications.push({
      id: uid('ntf'),
      companyId: emp.companyId,
      empId: emp.id,
      kind: 'overtime_reminder',
      payload: { shiftId: shift.id, hours: Math.round(hours * 100) / 100 },
      createdAt: nowIso(),
      deliveredAt: nowIso(),
      acknowledgedAt: null,
    });
    return true;
  }

  /**
   * Close the employee's shift if it has run past the company's maximum: no
   * hours, an audit row for the employer, a notification for the worker. The
   * API does this in `closeOverlongShifts`, hourly and on every read.
   */
  private closeOverlongShift(db: LocalDb, emp: Employee, settings: CompanySettings): boolean {
    const shift = this.openShift(db, emp.id);
    if (!shift || !isOverlong(shift, settings)) return false;
    const max = maxShiftHours(settings);
    const day = shift.clockInAt.slice(0, 10);
    const h = emp.worklog[day]?.h ?? 0;
    shift.clockOutAt = new Date(new Date(shift.clockInAt).getTime() + max * 3_600_000).toISOString();
    db.editLog.unshift({
      id: uid('edit'),
      companyId: emp.companyId,
      empId: emp.id,
      date: day, oldH: h, newH: h,
      who: 'Klukka',
      source: 'employer',
      note: `Sjálfvirk útstimplun — vakt stóð opin lengur en ${fmtH(max)} klst. Engar stundir skráðar; skráið raunverulegan vinnutíma.`,
      ts: nowIso(),
    });
    db.notifications
      .filter((n) => (n.payload.shiftId as string) === shift.id && !n.acknowledgedAt)
      .forEach((n) => { n.acknowledgedAt = nowIso(); });
    db.notifications.push({
      id: uid('ntf'),
      companyId: emp.companyId,
      empId: emp.id,
      kind: 'shift_closed',
      payload: { shiftId: shift.id, date: day, hoursOpen: Math.round(hoursOpen(shift) * 10) / 10, maxHours: max },
      createdAt: nowIso(),
      deliveredAt: nowIso(),
      acknowledgedAt: null,
    });
    return true;
  }

  // ---- employee portal ----

  private employeeSnapshot(db: LocalDb, session: Session): EmployeeSnapshot {
    const company = this.company(db, session.companyId);
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    const settings = this.settingsFor(company);
    const closed = this.closeOverlongShift(db, emp, settings);
    if (this.ensureOvertimeNotification(db, emp, settings) || closed) this.save(db);
    return clone({
      serverToday: this.today(),
      company: { ...company, settings },
      employee: emp,
      shift: this.openShift(db, emp.id) ?? null,
      grant: this.activeGrant(db, emp.id),
      requests: db.requests.filter((r) => r.empId === emp.id),
      timesheets: db.timesheets.filter((t) => t.empId === emp.id),
      adjustments: db.adjustments.filter((a) => a.empId === emp.id),
      edits: db.editLog.filter((e) => e.empId === emp.id),
      notifications: db.notifications.filter((n) => n.empId === emp.id && !n.acknowledgedAt),
      orlof: db.orlofEntries.filter((o) => o.empId === emp.id),
      payslips: this.payslipsFor(db, session.companyId, emp.id),
    });
  }

  /**
   * The approved periods this employee has a payslip for.
   *
   * Recalculated rather than looked up, so the amount in the list and the
   * amount on the PDF come from the same call and cannot disagree.
   */
  private payslipsFor(db: LocalDb, companyId: string, empId: string): PayslipSummary[] {
    const out: PayslipSummary[] = [];
    for (const p of db.payrollPeriods) {
      if (p.companyId !== companyId || p.status !== 'approved') continue;
      const line = this.computeRun(db, companyId, { start: p.periodStart, end: p.periodEnd }, empId).lines[0];
      // No hours in the period means no payslip, rather than a slip reading zero.
      if (!line) continue;
      out.push({
        periodKey: p.periodKey,
        periodStart: p.periodStart,
        periodEnd: p.periodEnd,
        payoutDate: p.payoutDate,
        approvedAt: p.approvedAt as string,
        gross: line.gross,
        net: line.net,
      });
    }
    return out.sort((a, b) => b.periodStart.localeCompare(a.periodStart));
  }

  async getEmployeeSnapshot(): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    return this.employeeSnapshot(this.db(), session);
  }

  async clockIn(): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    if (this.openShift(db, session.empId!)) throw new ApiError('Already clocked in', 409, 'conflict');
    db.shifts.push({
      id: uid('shift'),
      companyId: session.companyId,
      empId: session.empId!,
      clockInAt: nowIso(),
      clockOutAt: null,
      otConfirmedAt: null,
    });
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async clockOut(): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const shift = this.openShift(db, session.empId!);
    if (!shift) throw new ApiError('Not clocked in', 409, 'conflict');
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    // Past the maximum it is a forgotten clock-out: closed with no hours.
    if (this.closeOverlongShift(db, emp, this.settingsFor(this.company(db, session.companyId)))) {
      this.save(db);
      return this.employeeSnapshot(db, session);
    }
    shift.clockOutAt = nowIso();
    // Settle the elapsed time into the worklog for the day the shift started.
    const day = shift.clockInAt.slice(0, 10);
    const hours = (new Date(shift.clockOutAt).getTime() - new Date(shift.clockInAt).getTime()) / 3600_000;
    const prev = emp.worklog[day]?.h ?? 0;
    emp.worklog[day] = { ...emp.worklog[day], h: Math.min(DAY_HOURS, Math.round((prev + hours) * 100) / 100) };
    // The reminder belongs to a shift that is over.
    db.notifications
      .filter((n) => (n.payload.shiftId as string) === shift.id && !n.acknowledgedAt)
      .forEach((n) => { n.acknowledgedAt = nowIso(); });
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async confirmOvertime(): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const shift = this.openShift(db, session.empId!);
    if (!shift) throw new ApiError('Not clocked in', 409, 'conflict');
    const settings = this.settingsFor(this.company(db, session.companyId));
    if (isOverlong(shift, settings)) {
      throw new ApiError(
        `A shift past ${maxShiftHours(settings)} hours cannot be confirmed; clock out instead`,
        409,
        'shift_too_long',
      );
    }
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    const hours = (Date.now() - new Date(shift.clockInAt).getTime()) / 3600_000;
    shift.otConfirmedAt = nowIso();
    db.overtimeConfirmations.unshift({
      id: uid('otc'),
      companyId: session.companyId,
      empId: emp.id,
      who: emp.name,
      hours: Math.round(hours * 10) / 10,
      ts: nowIso(),
    });
    db.notifications
      .filter((n) => (n.payload.shiftId as string) === shift.id && !n.acknowledgedAt)
      .forEach((n) => { n.acknowledgedAt = nowIso(); });
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async updateMyProfile(patch: SelfServicePatch): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    // Only the self-service fields — wage and identity stay employer-owned.
    const allowed: Array<keyof SelfServicePatch> = [
      'phone', 'email', 'address', 'bank', 'union', 'pensionFund',
      'creditUtil', 'priorUnusedCredit', 'extraPensionPct', 'extraPensionFund', 'lang',
    ];
    const target = emp as unknown as Record<string, unknown>;
    for (const k of allowed) {
      if (patch[k] !== undefined) target[k] = patch[k];
    }
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async editMyHours(date: string, hours: number): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    const grant = this.activeGrant(db, emp.id);
    if (!grant || date < grant.from || date > grant.to) {
      throw new ApiError('Backdated editing is not open for that date', 403, 'grant_required');
    }
    const oldH = emp.worklog[date]?.h ?? 0;
    emp.worklog[date] = { ...emp.worklog[date], h: hours };
    db.editLog.unshift({
      id: uid('edit'),
      companyId: session.companyId,
      empId: emp.id,
      date, oldH, newH: hours,
      who: emp.name,
      source: 'employee',
      note: '',
      ts: nowIso(),
    });
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async signTimesheet(input: { periodKey: string; label: string; hours: number; signature: string }): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    db.timesheets.unshift({
      id: uid('ts'),
      companyId: session.companyId,
      empId: emp.id,
      periodKey: input.periodKey,
      label: input.label,
      hours: input.hours,
      status: 'submitted',
      trail: [{ who: input.signature, role: 'employee', action: 'signed & submitted', ts: nowIso() }],
    });
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async createRequest(input: NewRequestInput): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, session.empId!);
    const req: LeaveRequest = {
      id: uid('req'),
      companyId: session.companyId,
      empId: emp.id,
      ...input,
      status: 'pending',
      trail: [{ who: emp.name, role: 'employee', action: 'submitted', ts: nowIso() }],
    };
    db.requests.unshift(req);
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  async acknowledgeNotification(id: string): Promise<EmployeeSnapshot> {
    const session = this.requireSession('employee');
    const db = this.db();
    const n = db.notifications.find((x) => x.id === id && x.empId === session.empId);
    if (n) n.acknowledgedAt = nowIso();
    this.save(db);
    return this.employeeSnapshot(db, session);
  }

  // ---- employer portal ----

  private employerSnapshot(db: LocalDb, session: Session): EmployerSnapshot {
    const company = this.company(db, session.companyId);
    const settings = this.settingsFor(company);
    const employees = db.employees.filter((e) => e.companyId === session.companyId);
    for (const emp of employees) {
      this.closeOverlongShift(db, emp, settings);
      if (!emp.inactive) this.ensureOvertimeNotification(db, emp, settings);
    }
    this.postOrlofAccruals(db, employees, settings);
    this.save(db);
    const grants: Record<string, EditGrant> = {};
    for (const e of employees) {
      const g = this.activeGrant(db, e.id);
      if (g) grants[e.id] = g;
    }
    return clone({
      serverToday: this.today(),
      company: { ...company, settings },
      employees,
      openShifts: db.shifts.filter((s) => s.companyId === session.companyId && s.clockOutAt === null),
      requests: db.requests.filter((r) => r.companyId === session.companyId),
      timesheets: db.timesheets.filter((t) => t.companyId === session.companyId),
      adjustments: db.adjustments.filter((a) => a.companyId === session.companyId),
      editLog: db.editLog.filter((e) => e.companyId === session.companyId),
      editGrants: grants,
      overtimeConfirmations: db.overtimeConfirmations.filter((c) => c.companyId === session.companyId),
      orlofEntries: db.orlofEntries.filter((o) => o.companyId === session.companyId),
      payrollPeriods: db.payrollPeriods
        .filter((p) => p.companyId === session.companyId)
        .map(({ companyId: _c, ...p }) => p),
      payslipDeliveries: Object.fromEntries(
        db.payrollPeriods
          .filter((p) => p.companyId === session.companyId)
          .map((p) => [
            p.periodKey,
            db.payslipDeliveries.filter((d) => d.periodId === p.id).map(({ periodId: _p, ...d }) => d),
          ]),
      ),
    });
  }

  async getEmployerSnapshot(): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    return this.employerSnapshot(this.db(), session);
  }

  async updateSettings(patch: Partial<CompanySettings>): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const company = this.company(db, session.companyId);
    company.settings = { ...company.settings, ...patch };
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async createEmployee(input: NewEmployeeInput): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    db.employees.push({
      id: uid('emp'),
      companyId: session.companyId,
      name: input.name,
      role: input.role ?? '',
      kennitala: input.kennitala,
      monthlyWage: input.monthlyWage,
      lang: input.lang ?? 'en',
      startDate: this.today(),
      phone: '', email: '', address: '', bank: '',
      union: 'Efling', pensionFund: 'Gildi',
      extraPensionPct: 0, extraPensionFund: null,
      creditUtil: 100, priorUnusedCredit: 0,
      agreementId: null, expectedHoursOverride: 0,
      worklog: {}, inactive: false,
    });
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async updateEmployee(empId: string, patch: EmployeePatch): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, empId);
    Object.assign(emp, patch);
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  /** History is never destroyed — an employee with records can only be archived. */
  private hasRecords(db: LocalDb, empId: string): boolean {
    const emp = db.employees.find((e) => e.id === empId);
    return (
      Object.keys(emp?.worklog ?? {}).length > 0 ||
      db.timesheets.some((t) => t.empId === empId) ||
      db.requests.some((r) => r.empId === empId) ||
      db.editLog.some((l) => l.empId === empId)
    );
  }

  async deactivateEmployee(empId: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    this.employeeOf(db, session.companyId, empId).inactive = true;
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async reactivateEmployee(empId: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    this.employeeOf(db, session.companyId, empId).inactive = false;
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async deleteEmployee(empId: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    this.employeeOf(db, session.companyId, empId);
    if (this.hasRecords(db, empId)) {
      throw new ApiError('Employee has records and cannot be deleted', 409, 'has_records');
    }
    db.employees = db.employees.filter((e) => e.id !== empId);
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async setEditGrant(empId: string, grant: Omit<EditGrant, 'empId' | 'by' | 'ts'> | null): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    db.editGrants = db.editGrants.filter((g) => g.empId !== empId);
    if (grant) {
      db.editGrants.push({ ...grant, empId, by: session.name, ts: nowIso() });
      db.notifications.push({
        id: uid('ntf'),
        companyId: session.companyId,
        empId,
        kind: 'edit_grant_opened',
        payload: { from: grant.from, to: grant.to, until: grant.until },
        createdAt: nowIso(),
        deliveredAt: nowIso(),
        acknowledgedAt: null,
      });
    }
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async decideRequest(id: string, status: RequestStatus): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const req = db.requests.find((r) => r.id === id && r.companyId === session.companyId);
    if (!req) throw new ApiError('Request not found', 404, 'not_found');
    req.status = status;
    req.trail.push({ who: session.name, role: 'employer', action: status, ts: nowIso() });
    // Approved absence writes straight into the worklog so payroll reflects it.
    if (status === 'approved' && (req.type === 'sick' || req.type === 'vacation')) {
      const emp = this.employeeOf(db, session.companyId, req.empId);
      for (const d = new Date(`${req.from}T12:00`); d <= new Date(`${req.to}T12:00`); d.setDate(d.getDate() + 1)) {
        const ds = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        if (d.getDay() === 0 || d.getDay() === 6) continue;
        emp.worklog[ds] = req.type === 'sick' ? { sick: true } : { vacation: true };
      }
    }
    db.notifications.push({
      id: uid('ntf'),
      companyId: session.companyId,
      empId: req.empId,
      kind: 'request_decided',
      payload: { requestId: req.id, type: req.type, status },
      createdAt: nowIso(),
      deliveredAt: nowIso(),
      acknowledgedAt: null,
    });
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async decideTimesheet(id: string, status: TimesheetStatus): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const ts = db.timesheets.find((t) => t.id === id && t.companyId === session.companyId);
    if (!ts) throw new ApiError('Timesheet not found', 404, 'not_found');
    ts.status = status;
    ts.trail.push({ who: session.name, role: 'employer', action: status, ts: nowIso() });
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async importWorklogs(csv: string): Promise<{ result: ImportResult; snapshot: EmployerSnapshot }> {
    const session = this.requireSession('employer');
    const db = this.db();
    const employees = db.employees.filter((e) => e.companyId === session.companyId);
    const errors: string[] = [];
    let imported = 0;

    csv.split(/\n+/).map((l) => l.trim()).filter(Boolean).forEach((line, i) => {
      const [kt, date, hours] = line.split(/[;,\t]/).map((x) => (x ?? '').trim());
      const emp = employees.find((e) => normalizeKennitala(e.kennitala) === normalizeKennitala(kt));
      const h = parseFloat((hours ?? '').replace(',', '.'));
      if (!emp) { errors.push(`${i + 1}: unknown kennitala "${kt}"`); return; }
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date ?? '')) { errors.push(`${i + 1}: bad date "${date}"`); return; }
      if (!Number.isFinite(h) || h < 0 || h > 24) { errors.push(`${i + 1}: bad hours "${hours}"`); return; }
      const oldH = emp.worklog[date]?.h ?? 0;
      emp.worklog[date] = { ...emp.worklog[date], h };
      db.editLog.unshift({
        id: uid('edit'),
        companyId: session.companyId,
        empId: emp.id,
        date, oldH, newH: h,
        who: `${session.name} / Import`,
        source: 'import',
        note: '',
        ts: nowIso(),
      });
      imported++;
    });

    this.save(db);
    return { result: { imported, skipped: errors.length, errors }, snapshot: this.employerSnapshot(db, session) };
  }

  async createAdjustment(input: { empId: string; date: string; amount: number; note: string }): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    this.employeeOf(db, session.companyId, input.empId);
    const adj: Adjustment = {
      id: uid('adj'),
      companyId: session.companyId,
      empId: input.empId,
      date: input.date,
      amount: Math.round(input.amount),
      note: input.note,
      who: session.name,
      ts: nowIso(),
    };
    db.adjustments.unshift(adj);
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  /**
   * Settle accrual for pay periods that have closed since the last posting —
   * the same job the API runs, on the same read path, for the same reason.
   *
   * Balances read correctly without it, because anything unposted is accrued
   * live from the worklog. Posting is what makes the record survive a pay rise.
   */
  private postOrlofAccruals(db: LocalDb, employees: Employee[], settings: CompanySettings): void {
    const today = this.today();
    for (const emp of employees) {
      if (emp.inactive) continue;
      const mine = db.orlofEntries.filter((o) => o.empId === emp.id);
      for (const draft of pendingAccruals(emp, settings, mine, today)) {
        // The API leans on a unique index for this; here it is an explicit check.
        const clash = mine.some((o) => o.periodKey === draft.periodKey && o.kind === draft.kind);
        if (clash) continue;
        const entry: OrlofEntry = {
          ...draft,
          id: uid('orlof'),
          companyId: emp.companyId,
          empId: emp.id,
          who: 'system:posting',
          ts: nowIso(),
        };
        db.orlofEntries.push(entry);
        mine.push(entry);
      }
    }
  }

  async addOrlofEntry(input: OrlofEntryInput): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, input.empId);
    if (!Number.isFinite(input.days) || input.days <= 0) {
      throw new ApiError('Enter a positive number of days', 400, 'bad_input');
    }
    if (input.kind === 'opening' && db.orlofEntries.some((o) => o.empId === emp.id && o.kind === 'opening')) {
      throw new ApiError('This employee already has an opening balance', 409, 'conflict');
    }
    const settings = this.settingsFor(this.company(db, session.companyId));
    const dayRate = orlofDayRate(emp, settings);
    // The caller sends a magnitude; `kind` decides the direction, so a stray
    // minus sign cannot turn a payout into a credit.
    const sign = input.kind === 'payout' ? -1 : 1;
    db.orlofEntries.push({
      id: uid('orlof'),
      companyId: session.companyId,
      empId: emp.id,
      orlofYear: orlofYearOf(input.date),
      date: input.date,
      kind: input.kind,
      periodKey: null,
      isk: sign * input.days * dayRate,
      days: sign * input.days,
      basisIsk: 0,
      pct: 0,
      dayRate,
      note: input.note,
      who: session.name,
      ts: nowIso(),
    });
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  // ---- payroll ----

  /**
   * Calculate a period from the current worklog.
   *
   * Nothing about the money is stored, here or in the API — the same engine
   * runs every time it is asked, so a correction to somebody's hours shows up
   * in the payslip the moment it is made.
   */
  private computeRun(db: LocalDb, companyId: string, period: { start: string; end: string }, empId?: string): PayrollRun {
    const company = this.company(db, companyId);
    const settings = this.settingsFor(company);
    return buildPayrollRun({
      company: { ...company, settings },
      employees: db.employees.filter(
        (e) => e.companyId === companyId
          && (!empId || e.id === empId)
          && (!e.inactive || Object.keys(e.worklog).length > 0),
      ),
      settings,
      period,
      adjustments: db.adjustments.filter((a) => a.companyId === companyId),
      orlofEntries: db.orlofEntries.filter((o) => o.companyId === companyId),
      today: this.today(),
    });
  }

  /** The period record, created as a draft the first time it is opened. */
  private ensurePeriod(db: LocalDb, session: Session, run: PayrollRun): StoredPeriod {
    let period = db.payrollPeriods.find(
      (p) => p.companyId === session.companyId && p.periodKey === run.periodKey,
    );
    if (!period) {
      period = {
        id: uid('pp'),
        companyId: session.companyId,
        periodKey: run.periodKey,
        periodStart: run.periodStart,
        periodEnd: run.periodEnd,
        payoutDate: run.payoutDate,
        status: 'draft',
        approvedBy: null,
        approvedAt: null,
        reopenedBy: null,
        reopenedAt: null,
        createdBy: session.name,
        createdAt: nowIso(),
      };
      db.payrollPeriods.unshift(period);
      this.save(db);
    }
    return period;
  }

  private periodView(db: LocalDb, session: Session, period: StoredPeriod, run: PayrollRun) {
    const { companyId: _companyId, ...rest } = period;
    return {
      period: rest,
      run,
      deliveries: db.payslipDeliveries.filter((d) => d.periodId === period.id).map(({ periodId: _p, ...d }) => d),
      snapshot: this.employerSnapshot(db, session),
    };
  }

  async payrollPeriod(input: { periodStart: string; periodEnd: string }) {
    const session = this.requireSession('employer');
    const db = this.db();
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd });
    const problems = reconcile(run);
    if (problems.length) throw new ApiError(`Payroll does not balance: ${problems[0]}`, 500, 'internal');
    return this.periodView(db, session, this.ensurePeriod(db, session, run), run);
  }

  async approvePayrollPeriod(input: { periodStart: string; periodEnd: string }) {
    const session = this.requireSession('employer');
    const db = this.db();
    // Recalculated at the moment of approval rather than trusting whatever the
    // screen last drew, which may predate a correction.
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd });
    const period = this.ensurePeriod(db, session, run);
    period.status = 'approved';
    period.approvedBy = session.name;
    period.approvedAt = nowIso();
    this.save(db);
    return this.periodView(db, session, period, run);
  }

  async reopenPayrollPeriod(input: { periodStart: string; periodEnd: string }) {
    const session = this.requireSession('employer');
    const db = this.db();
    const key = `${input.periodStart}_${input.periodEnd}`;
    const period = db.payrollPeriods.find((p) => p.companyId === session.companyId && p.periodKey === key);
    if (!period) throw new ApiError('That period has not been opened', 404, 'not_found');
    period.status = 'draft';
    period.approvedBy = null;
    period.approvedAt = null;
    period.reopenedBy = session.name;
    period.reopenedAt = nowIso();
    this.save(db);
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd });
    return this.periodView(db, session, period, run);
  }

  /**
   * The PDF renderer, loaded on demand.
   *
   * A dynamic import so pdf-lib and the embedded font become their own chunk
   * rather than part of the console's first paint. In an `http` build this
   * whole class is dropped at compile time, so a deployed portal never carries
   * any of it — the server renders instead, from the same module.
   */
  private async renderer() {
    return import('@klukka/payroll-pdf');
  }

  private stamp(): string {
    return new Date().toISOString().slice(0, 19).replace('T', ' ');
  }

  private isDraft(db: LocalDb, companyId: string, key: string): boolean {
    return db.payrollPeriods.find((p) => p.companyId === companyId && p.periodKey === key)?.status !== 'approved';
  }

  async payrollReportPdf(input: { periodStart: string; periodEnd: string }, lang: 'is' | 'en'): Promise<Uint8Array> {
    const session = this.requireSession('employer');
    const db = this.db();
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd });
    const { renderPayrollReport } = await this.renderer();
    return renderPayrollReport(run, lang, {
      generatedAt: this.stamp(),
      draft: this.isDraft(db, session.companyId, run.periodKey),
    });
  }

  async payslipBatchPdf(input: { periodStart: string; periodEnd: string }): Promise<Uint8Array> {
    const session = this.requireSession('employer');
    const db = this.db();
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd });
    const { renderPayslipBatch } = await this.renderer();
    return renderPayslipBatch(run, {
      generatedAt: this.stamp(),
      draft: this.isDraft(db, session.companyId, run.periodKey),
    });
  }

  async payslipPdf(input: { periodStart: string; periodEnd: string }, empId: string): Promise<Uint8Array> {
    const session = this.requireSession('employer');
    const db = this.db();
    const run = this.computeRun(db, session.companyId, { start: input.periodStart, end: input.periodEnd }, empId);
    const line = run.lines.find((l) => l.empId === empId);
    if (!line) throw new ApiError('That employee has no pay in this period', 404, 'not_found');
    const { renderPayslip } = await this.renderer();
    return renderPayslip(run, line, {
      generatedAt: this.stamp(),
      draft: this.isDraft(db, session.companyId, run.periodKey),
    });
  }

  /** An employee's own payslip, for an approved period only. */
  async myPayslipPdf(periodKey: string): Promise<Uint8Array> {
    const session = this.requireSession('employee');
    const db = this.db();
    const period = db.payrollPeriods.find(
      (p) => p.companyId === session.companyId && p.periodKey === periodKey && p.status === 'approved',
    );
    if (!period) throw new ApiError('No payslip has been issued for that period', 404, 'not_found');
    const run = this.computeRun(
      db, session.companyId,
      { start: period.periodStart, end: period.periodEnd },
      session.empId ?? undefined,
    );
    const line = run.lines[0];
    if (!line) throw new ApiError('You had no pay in that period', 404, 'not_found');
    const { renderPayslip } = await this.renderer();
    return renderPayslip(run, line, { generatedAt: this.stamp() });
  }

  // ---- work reports ----

  /** Everything a sheet needs, from the local database. */
  private timesheetContext(db: LocalDb, companyId: string, empId?: string) {
    const company = this.company(db, companyId);
    return {
      company,
      settings: this.settingsFor(company),
      timesheets: db.timesheets.filter((t) => (!empId || t.empId === empId)
        && db.employees.some((e) => e.id === t.empId && e.companyId === companyId)),
      edits: db.editLog.filter((e) => (!empId || e.empId === empId) && e.companyId === companyId),
      today: this.today(),
    };
  }

  async timesheetPdf(input: { periodStart: string; periodEnd: string }, empId: string, lang: 'is' | 'en') {
    const session = this.requireSession('employer');
    const db = this.db();
    const ctx = this.timesheetContext(db, session.companyId);
    const employee = db.employees.find((e) => e.id === empId && e.companyId === session.companyId);
    if (!employee) throw new ApiError('No such employee', 404, 'not_found');
    const doc = buildTimesheet({
      employee, settings: ctx.settings, period: { start: input.periodStart, end: input.periodEnd },
      timesheets: ctx.timesheets, edits: ctx.edits, today: ctx.today,
    });
    const { renderTimesheet } = await this.renderer();
    return renderTimesheet(doc, ctx.company, lang, { generatedAt: this.stamp() });
  }

  async timesheetBatchPdf(input: { periodStart: string; periodEnd: string }, lang: 'is' | 'en') {
    const session = this.requireSession('employer');
    const db = this.db();
    const ctx = this.timesheetContext(db, session.companyId);
    const docs = buildTimesheets(
      db.employees.filter((e) => e.companyId === session.companyId),
      {
        settings: ctx.settings, period: { start: input.periodStart, end: input.periodEnd },
        timesheets: ctx.timesheets, edits: ctx.edits, today: ctx.today,
      },
    );
    const { renderTimesheetBatch } = await this.renderer();
    return renderTimesheetBatch(docs, ctx.company, lang, { generatedAt: this.stamp() });
  }

  async myTimesheetPdf(input: { periodStart: string; periodEnd: string }) {
    const session = this.requireSession('employee');
    const db = this.db();
    const employee = this.employeeOf(db, session.companyId, session.empId!);
    const ctx = this.timesheetContext(db, session.companyId, employee.id);
    const doc = buildTimesheet({
      employee, settings: ctx.settings, period: { start: input.periodStart, end: input.periodEnd },
      timesheets: ctx.timesheets, edits: ctx.edits, today: ctx.today,
    });
    const { renderTimesheet } = await this.renderer();
    // The employee's own language: the point of the sheet is that the person
    // signing it can read it.
    return renderTimesheet(doc, ctx.company, employee.lang, { generatedAt: this.stamp() });
  }

  async notifyPayslips(): Promise<never> {
    // Local mode has no mail transport, and pretending otherwise would let
    // somebody believe an employee had been told.
    throw new ApiError(
      'Payslip notifications cannot be sent in local mode — download the payslips instead',
      503,
      'email_not_configured',
    );
  }

  async emailAvailable(): Promise<boolean> {
    return false;
  }

  async deleteAdjustment(id: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    db.adjustments = db.adjustments.filter((a) => !(a.id === id && a.companyId === session.companyId));
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async clockOutEmployee(empId: string, at: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, empId);
    const shift = this.openShift(db, emp.id);
    if (!shift) throw new ApiError('This employee is not clocked in', 409, 'not_clocked_in');
    const end = Date.parse(at);
    if (Number.isNaN(end)) throw new ApiError('at, the time the shift ended, is required', 400, 'bad_input');
    if (end > Date.now() + 5 * 60_000) throw new ApiError('The clock-out time is in the future', 400, 'in_future');
    const hours = Math.round(((end - new Date(shift.clockInAt).getTime()) / 3_600_000) * 100) / 100;
    if (hours < 0) throw new ApiError('The clock-out time is before the clock-in', 400, 'before_clock_in');
    if (hours > DAY_HOURS) {
      throw new ApiError(`A shift is recorded on one day, which holds at most ${DAY_HOURS} hours`, 400, 'too_long');
    }
    const day = shift.clockInAt.slice(0, 10);
    const oldH = emp.worklog[day]?.h ?? 0;
    const newH = Math.round((oldH + hours) * 100) / 100;
    if (newH > DAY_HOURS) {
      throw new ApiError(`${day} already has ${oldH} hours; this shift would take it past ${DAY_HOURS}`, 400, 'day_full');
    }
    shift.clockOutAt = new Date(end).toISOString();
    emp.worklog[day] = { ...emp.worklog[day], h: newH };
    db.editLog.unshift({
      id: uid('edit'),
      companyId: session.companyId,
      empId: emp.id,
      date: day, oldH, newH,
      who: session.name,
      source: 'employer',
      note: 'Útstimplun skráð af stjórnanda',
      ts: nowIso(),
    });
    db.notifications
      .filter((n) => (n.payload.shiftId as string) === shift.id && !n.acknowledgedAt)
      .forEach((n) => { n.acknowledgedAt = nowIso(); });
    this.save(db);
    return this.employerSnapshot(db, session);
  }

  async correctHours(input: HourCorrectionInput): Promise<{ oldH: number; newH: number; snapshot: EmployerSnapshot }> {
    const session = this.requireSession('employer');
    const db = this.db();
    const emp = this.employeeOf(db, session.companyId, input.empId);
    if (!Number.isFinite(input.deltaHours) || input.deltaHours === 0) {
      throw new ApiError('Enter a non-zero ± hour correction', 400, 'bad_input');
    }
    const oldH = emp.worklog[input.date]?.h ?? 0;
    const newH = Math.min(24, Math.max(0, oldH + input.deltaHours));
    emp.worklog[input.date] = { ...emp.worklog[input.date], h: newH };
    db.editLog.unshift({
      id: uid('edit'),
      companyId: session.companyId,
      empId: emp.id,
      date: input.date, oldH, newH,
      who: session.name,
      source: 'employer',
      note: input.note,
      ts: nowIso(),
    });
    this.save(db);
    return { oldH, newH, snapshot: this.employerSnapshot(db, session) };
  }

  async deleteHourCorrection(id: string): Promise<EmployerSnapshot> {
    const session = this.requireSession('employer');
    const db = this.db();
    const entry = db.editLog.find((l) => l.id === id && l.companyId === session.companyId);
    if (!entry) throw new ApiError('Correction not found', 404, 'not_found');
    const emp = this.employeeOf(db, session.companyId, entry.empId);
    emp.worklog[entry.date] = { ...emp.worklog[entry.date], h: entry.oldH };
    db.editLog = db.editLog.filter((l) => l.id !== id);
    this.save(db);
    return this.employerSnapshot(db, session);
  }
}

/** Hours a running shift has accumulated so far. */
export function liveHours(shift: Shift | null | undefined): number {
  if (!shift || shift.clockOutAt) return 0;
  // Never below zero. The clock-in is stamped by the server and read against
  // the browser's clock; a phone a few seconds behind would otherwise show a
  // negative shift, and negative hours turn into negative pay on the screen
  // the moment somebody clocks in.
  return Math.max(0, (Date.now() - new Date(shift.clockInAt).getTime()) / 3600_000);
}

/** Convenience re-exports for portals that need to reason about the period. */
export { daysFor, fmtH, periodInfo };
export type { Company, CompanySettings, Employee, EditLogEntry, NotificationEvent, OvertimeConfirmation, Timesheet };
